import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Mini courbe d'évolution du prix + badge variation (vs le prix précédent).
 *
 * Props:
 *  - data: { points: [{ date, unit_price, variation_pct }], last_variation_pct, ... }
 *  - showBadge: bool (défaut: true)
 *  - width / height: dimensions (défaut 72 x 22)
 */
export default function PriceSparkline({ data, showBadge = true, width = 72, height = 22 }) {
    const chart = useMemo(() => {
        if (!data || !data.points || data.points.length < 2) return null;
        const prices = data.points.map(p => p.unit_price).filter(p => typeof p === 'number');
        if (prices.length < 2) return null;
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const range = max - min || 1;
        const coords = prices.map((p, i) => ({
            x: (i / (prices.length - 1)) * width,
            y: height - ((p - min) / range) * (height - 2) - 1,
        }));
        const path = `M${coords.map(c => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' L')}`;
        return { path, last: coords[coords.length - 1] };
    }, [data, width, height]);

    if (!chart) {
        return (
            <svg width={width} height={height} className="opacity-30">
                <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke="#cbd5e1" strokeDasharray="2,2" strokeWidth="1" />
            </svg>
        );
    }

    const variation = data?.last_variation_pct;
    const up = variation != null && variation > 0.1;
    const down = variation != null && variation < -0.1;
    const stroke = up ? '#dc2626' : down ? '#059669' : '#64748b';

    return (
        <div className="flex items-center gap-1.5" data-testid="price-sparkline">
            <svg width={width} height={height} className="flex-shrink-0">
                <path d={chart.path} fill="none" stroke={stroke} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                <circle cx={chart.last.x} cy={chart.last.y} r="2" fill={stroke} />
            </svg>
            {showBadge && variation != null && Math.abs(variation) > 0.1 && (
                <span
                    className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-bold ${
                        up ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                    }`}
                    title={`Variation : ${variation > 0 ? '+' : ''}${variation}% depuis la précédente réception`}
                >
                    {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    {variation > 0 ? '+' : ''}{variation.toFixed(1)}%
                </span>
            )}
        </div>
    );
}
